import React, { useState, useEffect } from 'react';
import { confirmSignUp } from 'aws-amplify/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const data = searchParams.get('data');
        const code = searchParams.get('code');

        if (!data || !code) {
          setStatus('error');
          setMessage('Invalid verification link. Please check your email and try again.');
          setLoading(false);
          return;
        }

        // Decode the data parameter
        const decodedData = JSON.parse(atob(data));
        const { userName, redirectUrl, region, clientId } = decodedData;

        // Confirm the sign up
        await confirmSignUp({
          username: userName,
          confirmationCode: code,
        });

        setStatus('success');
        setMessage('Account verified successfully! Redirecting to GradedWash...');
        setLoading(false);

        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/profile');
        }, 3000);

      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        
        if (error.message.includes('User cannot be confirm. Current status is CONFIRMED')) {
          setMessage('Your account is already verified! Redirecting to GradedWash...');
          setTimeout(() => {
            navigate('/profile');
          }, 3000);
        } else if (error.message.includes('Invalid code')) {
          setMessage('Invalid verification code. Please check your email and try again.');
        } else if (error.message.includes('CodeMismatchException')) {
          setMessage('Verification code has expired. Please request a new verification email.');
        } else {
          setMessage('Verification failed. Please try again or contact support.');
        }
        setLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 p-6 text-white">
            <h1 className="text-2xl font-bold">Verifying Your Account</h1>
            <p className="opacity-90">Please wait while we verify your email address...</p>
          </div>
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying your account...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className={`p-6 text-white ${
          status === 'success' 
            ? 'bg-gradient-to-r from-green-600 to-green-700' 
            : status === 'error' 
            ? 'bg-gradient-to-r from-red-600 to-red-700'
            : 'bg-gradient-to-r from-purple-600 to-indigo-700'
        }`}>
          <h1 className="text-2xl font-bold">
            {status === 'success' ? '✅ Success!' : status === 'error' ? '❌ Verification Failed' : 'Verifying Account'}
          </h1>
          <p className="opacity-90">{message}</p>
        </div>
        <div className="p-6">
          {status === 'success' && (
            <div className="text-center">
              <div className="text-green-600 text-6xl mb-4">✅</div>
              <h2 className="text-xl font-semibold mb-4">Welcome to GradedWash!</h2>
              <p className="text-gray-600 mb-6">
                Your account has been verified successfully. You'll be redirected to your profile page shortly.
              </p>
              <div className="animate-pulse">
                <div className="h-2 bg-green-200 rounded mb-2"></div>
                <div className="h-2 bg-green-200 rounded mb-2"></div>
                <div className="h-2 bg-green-200 rounded"></div>
              </div>
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-center">
              <div className="text-red-600 text-6xl mb-4">❌</div>
              <h2 className="text-xl font-semibold mb-4">Verification Failed</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/signup')}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Try Signing Up Again
                </button>
                <button 
                  onClick={() => navigate('/')}
                  className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Go to Homepage
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 